import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Users, DollarSign, Video, LogOut, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    loadData();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = async () => {
    try {
      const [analyticsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() })
      ]);

      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">Manage users and subscriptions</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin/users/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-white border-gray-200 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.users.total}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {analytics.users.free} free, {analytics.users.pro} pro
                  </p>
                </div>
                <Users className="w-12 h-12 text-blue-600" />
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Revenue</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">${analytics.payments.total_revenue}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {analytics.payments.successful} payments
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-green-600" />
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Recordings</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.recordings.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Total created</p>
                </div>
                <Video className="w-12 h-12 text-purple-600" />
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Custom Pricing</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.users.custom_pricing}</p>
                  <p className="text-xs text-gray-500 mt-1">Enterprise users</p>
                </div>
                <DollarSign className="w-12 h-12 text-orange-600" />
              </div>
            </Card>
          </div>
        )}

        {/* Users Table */}
        <Card className="bg-white border-gray-200 shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">All Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recordings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.display_name || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.subscription_tier === 'pro' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.subscription_tier}
                      </span>
                      {user.custom_price && (
                        <span className="ml-2 text-xs text-orange-600">${user.custom_price}/mo</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.total_recordings || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.subscription_tier === 'free' 
                        ? `${user.recording_count_current_month || 0}/5`
                        : 'Unlimited'
                      }
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user.user_id}`)}
                        className="bg-gray-600 hover:bg-gray-700 text-white"
                      >
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
